import { useState } from 'react';
import { OTPInput } from '@/components/auth/OTPInput';
import { sendOTPForSettings, verifyOTPForSettings } from '@/services/settings';

interface EmailVerificationStepProps {
  userEmail: string;
  userName: string;
  onVerified: () => void;
  onCancel: () => void;
}

export function EmailVerificationStep({ 
  userEmail, 
  userName, 
  onVerified, 
  onCancel 
}: EmailVerificationStepProps) {
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();
  const [nextAllowedAt, setNextAllowedAt] = useState<string | undefined>();

  const handleSendOTP = async () => {
    setIsResending(true);
    setOtpError('');

    try {
      const result = await sendOTPForSettings(userEmail, 'reset_password', userName);
      
      if (result.success) {
        setShowOTPInput(true);
        setRemainingAttempts(result.data?.remainingAttempts);
      } else {
        setOtpError(result.error || '인증번호 발송에 실패했습니다.');
        setRemainingAttempts(result.data?.remainingAttempts);
        setNextAllowedAt(result.data?.nextAllowedAt);
      }
    } catch (error) {
      console.error('OTP send error:', error);
      setOtpError('인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setIsOTPLoading(true);
    setOtpError('');

    try {
      const result = await verifyOTPForSettings(userEmail, otp, 'reset_password');
      
      if (result.success) {
        onVerified();
      } else {
        setOtpError(result.error || '인증번호가 올바르지 않습니다.');
        setRemainingAttempts(result.data?.remainingAttempts);
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      setOtpError('인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsOTPLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h4 className="text-base font-semibold text-gray-900 mb-2">
          본인 인증
        </h4>
        <p className="text-sm text-gray-600">
          비밀번호 변경을 위해 이메일로 본인 인증을 진행합니다
        </p>
      </div>

      {/* 이메일 표시 */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          인증 이메일
        </label>
        <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-center">
          {userEmail}
        </div>
      </div>

      {!showOTPInput ? (
        <div className="space-y-4">
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h5 className="text-sm font-medium text-yellow-800 mb-1">보안 안내</h5>
                <p className="text-sm text-yellow-700">
                  계정 보안을 위해 본인 확인이 필요합니다. 위 이메일 주소로 인증번호를 발송해드립니다.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              취소
            </button>
            
            <button
              onClick={handleSendOTP}
              disabled={isResending}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                !isResending
                  ? 'bg-[#4CD964] text-white hover:bg-[#45B85C]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isResending ? '발송 중...' : '인증번호 받기'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-sm text-gray-600 mb-4 text-center">
            <strong>{userEmail}</strong>로 발송된 인증번호를 입력해주세요
          </div>
          
          <OTPInput
            onComplete={handleVerifyOTP}
            onResend={handleSendOTP}
            isLoading={isOTPLoading}
            isResending={isResending}
            error={otpError}
            remainingAttempts={remainingAttempts}
            nextAllowedAt={nextAllowedAt}
          />
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={onCancel}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}